/**
 * useActivityFeed — fetches the paginated activity feed and the caller's read
 * watermark from `xan_get_recent_activity` via the InsForge SDK.
 */
import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import type { RawActivityRow } from "@/features/notifications/activity-mapping";
import { mapActivityRow } from "@/features/notifications/activity-mapping";
import { parseNotificationEnvelope } from "@/features/notifications/envelope";
import { activityKeys } from "@/features/notifications/hooks/query-keys";
import type { AppNotification } from "@/features/notifications/types";
import { getBackendClient } from "@/lib/backend";

const backend = getBackendClient();

const DEFAULT_LIMIT = 20;
const DEFAULT_OFFSET = 0;

const activityRowSchema: z.ZodType<RawActivityRow> = z.object({
  action: z.string(),
  actor_source: z.string(),
  created_at: z.string(),
  description: z.string().nullable(),
  entity_id: z.string(),
  entity_type: z.string(),
  id: z.string(),
  metadata: z.record(z.string(), z.unknown()).nullable(),
  profile_id: z.string().nullable(),
});

const activityFeedResponseSchema = z.object({
  items: z.array(activityRowSchema).nullable(),
  last_read_at: z.string().nullable(),
  limit: z.number().int().nonnegative(),
  offset: z.number().int().nonnegative(),
});

export interface ActivityFeed {
  lastReadAt: string | null;
  notifications: AppNotification[];
}

export function useActivityFeed(limit: number = DEFAULT_LIMIT, offset: number = DEFAULT_OFFSET) {
  return useQuery({
    queryKey: activityKeys.feed(limit, offset),
    queryFn: async (): Promise<ActivityFeed> => {
      const { data, error } = await backend.data.rpc("xan_get_recent_activity", {
        p_limit: limit,
        p_offset: offset,
      });

      if (error) {
        throw error;
      }

      const response = parseNotificationEnvelope("activity feed", activityFeedResponseSchema, data);

      return {
        notifications: (response.items ?? []).map(mapActivityRow),
        lastReadAt: response.last_read_at ?? null,
      };
    },
    staleTime: 30_000,
  });
}
